import { useMemo, useState } from "react";
import { channelGroups, channels } from "@/mocks/homeData";

type ChannelDirectoryProps = {
  query: string;
  onQueryChange: (value: string) => void;
};

const filters = [
  { id: "all", label: "全部频道" },
  ...channelGroups.map((g) => ({ id: g.id, label: g.title })),
];

export default function ChannelDirectory({ query, onQueryChange }: ChannelDirectoryProps) {
  const [activeFilter, setActiveFilter] = useState("all");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return channels.filter((channel) => {
      const matchGroup = activeFilter === "all" || channel.groupId === activeFilter;
      const matchKeyword =
        keyword === "" ||
        channel.name.toLowerCase().includes(keyword) ||
        channel.desc.toLowerCase().includes(keyword) ||
        channel.group.toLowerCase().includes(keyword);
      return matchGroup && matchKeyword;
    });
  }, [query, activeFilter]);

  return (
    <section id="channels" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 font-label text-xs text-primary-600 tracking-widest">
            <i className="ri-layout-grid-line" />
            CHANNELS
          </span>
          <h2 className="mt-3 font-heading font-black text-2xl md:text-4xl text-foreground-950">
            频道导览：各板块与频道分区
          </h2>
          <p className="mt-4 font-body text-sm md:text-base text-foreground-600 leading-relaxed">
            网站按主题划分为四大板块，共 12 个内容频道。每个频道都标注了收录数量与内容方向，
            点击标题即可跳转到对应入口。
          </p>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setActiveFilter(filter.id)}
                className={`cursor-pointer whitespace-nowrap font-label text-sm px-4 py-2 rounded-full border transition-colors ${
                  activeFilter === filter.id
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 border border-background-200 lg:w-72">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-filter-3-line text-base" />
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="筛选频道"
              aria-label="筛选频道"
              className="w-full bg-transparent font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((channel) => (
              <a
                key={channel.name}
                href="#sitemap"
                className="group cursor-pointer rounded-lg border border-background-200 bg-background-100 p-5 hover:border-primary-300 hover:bg-primary-50/60 transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${channel.icon} text-xl`} />
                  </span>
                  <span className="font-label text-xs text-foreground-500 bg-background-50 border border-background-200 px-2.5 py-1 rounded-full">
                    {channel.count} 篇
                  </span>
                </div>
                <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-700">
                  {channel.name}
                </h3>
                <p className="mt-2 font-body text-sm text-foreground-600 leading-relaxed flex-1">
                  {channel.desc}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-label text-xs text-secondary-600 bg-secondary-50 border border-secondary-200 px-2.5 py-1 rounded-full">
                    {channel.group}
                  </span>
                  <span className="font-label text-sm text-primary-600 flex items-center gap-1">
                    进入频道
                    <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="mt-4 font-heading font-bold text-lg text-foreground-900">
              没有找到「{query}」相关的频道
            </p>
            <p className="mt-2 font-body text-sm text-foreground-500">
              换个关键词试试，或点击下方按钮查看全部频道。
            </p>
            <button
              type="button"
              onClick={() => {
                onQueryChange("");
                setActiveFilter("all");
              }}
              className="mt-5 whitespace-nowrap cursor-pointer font-label text-sm px-5 py-2.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              查看全部频道
            </button>
          </div>
        )}
      </div>
    </section>
  );
}